import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Gamepad, Sparkles, BookOpen, Trophy, Users, Heart } from 'lucide-react';
import GoogleAdsense from '@/components/GoogleAdsense';

const PokeRogue = () => {
  const intl = useIntl();

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-slate-900 to-indigo-950">
      <Helmet>
        <title>PokéRogue - A Unique Pokémon Rogue-lite Experience | Pokedle</title>
        <meta name="description" content="Experience PokéRogue, the exciting Pokémon Rogue-lite game with procedurally generated worlds. Play directly in your browser for free and embark on an epic adventure!" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1614728263952-84ea256f9679?q=80&w=1200')` }}></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="inline-block bg-indigo-600 text-white px-4 py-1 rounded-full mb-4">
              <div className="flex items-center">
                <Gamepad className="h-4 w-4 mr-2" />
                <span>Play Now</span>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-4 text-white">
              Poké<span className="text-indigo-400">Rogue</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-200 max-w-3xl">
              A unique rogue-lite experience set in the Pokémon universe, featuring procedurally generated worlds and strategic gameplay.
            </p>
            <a 
              href="#game-section" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-8 rounded-lg transform transition-all hover:scale-105 flex items-center"
            >
              <Gamepad className="mr-2" />
              Play PokéRogue Now
            </a>
          </div>
        </div>
      </section>

      <GoogleAdsense slot="1234567890" style={{ display: 'block', marginBottom: '2rem' }} />

      {/* Game Section */}
      <section id="game-section" className="py-16 container mx-auto px-4">
        <div className="mb-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Experience PokéRogue</h2>
          <p className="text-indigo-200">Play directly in your browser - no downloads required!</p>
        </div>
        <div className="game-wrapper bg-black rounded-lg shadow-2xl border-2 border-indigo-500 overflow-hidden mx-auto">
          <iframe 
            src="https://pokerogue.online/" 
            frameBorder="0" 
            allow="gamepad *;"
            className="w-full h-full"
            title="PokéRogue Game"
          ></iframe>
        </div>
      </section>

      <GoogleAdsense slot="0987654321" style={{ display: 'block', marginTop: '2rem', marginBottom: '2rem' }} />

      {/* Features Section */}
      <section className="py-16 bg-indigo-900 bg-opacity-30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">Game Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg border border-indigo-600 transform transition-all hover:scale-105">
              <div className="text-indigo-400 mb-4">
                <Sparkles className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Procedurally Generated Worlds</h3>
              <p className="text-indigo-200">Every playthrough offers a unique world to explore with different maps, encounters, and challenges.</p>
            </div>
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg border border-indigo-600 transform transition-all hover:scale-105">
              <div className="text-indigo-400 mb-4">
                <Trophy className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Strategic Gameplay</h3>
              <p className="text-indigo-200">Test your skills with challenging battles that require careful planning and resource management.</p>
            </div>
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg border border-indigo-600 transform transition-all hover:scale-105">
              <div className="text-indigo-400 mb-4">
                <Users className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">Build Your Team</h3>
              <p className="text-indigo-200">Capture wild Pokémon and build a powerful team to overcome increasingly difficult challenges.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Game Description */}
      <section className="py-16 container mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">What is PokéRogue?</h2>
          <div className="w-24 h-1 bg-indigo-500 mx-auto mt-4 mb-6"></div>
          <p className="text-xl text-indigo-200 max-w-4xl mx-auto">Discover a fresh take on the Pokémon universe with rogue-lite elements</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="bg-indigo-900 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-4">A Pokemon Rogue-lite Game</h3>
            <p className="text-indigo-200 mb-4">
              PokéRogue is a unique rogue-lite experience set in the Pokémon universe. Unlike traditional Pokémon games, PokéRogue features procedurally generated worlds, meaning the map and encounters change with every playthrough. This injects a hefty dose of strategy and replayability, forcing you to adapt to ever-changing situations.
            </p>
            <p className="text-indigo-200 mb-4">
              While it doesn't have permadeath like some Roguelike games, PokéRogue offers a challenging experience that requires careful planning and resource management. The ever-changing environments and strategic depth make it perfect for players who have mastered the mainline Pokémon games and crave something new.
            </p>
            <p className="text-indigo-200">
              At its core, PokéRogue retains the turn-based battling system familiar to Pokémon fans. You'll encounter wild Pokémon, capture them to build your team, and engage in strategic battles. However, the rogue-lite twist comes in the form of procedurally generated maps, meaning each playthrough offers a unique world to explore.
            </p>
          </div>
          <div className="bg-indigo-900 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-2xl font-bold text-white mb-4">Who Created PokéRogue?</h3>
            <p className="text-indigo-200 mb-4">
              The creative minds behind PokéRogue remain shrouded in a bit of mystery. However, the game's active development can be found on GitHub, hinting at a passionate community effort. This collaborative spirit adds another layer of intrigue to the ever-evolving world of PokéRogue.
            </p>
            <h3 className="text-2xl font-bold text-white mb-4 mt-8">Who Is This Game For?</h3>
            <p className="text-indigo-200">
              If you're a Pokémon fan yearning for a fresh challenge that tests your strategic thinking and adaptability, then PokéRogue is for you. The ever-changing environments and strategic depth make it perfect for players who have mastered the mainline Pokémon games and crave something new and exciting. It's also great for fans of roguelike and roguelite games who want to experience that gameplay style in the Pokémon universe.
            </p>
          </div>
        </div>

        <div className="bg-indigo-800 bg-opacity-20 p-8 rounded-lg shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">How to Play PokéRogue Online</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-indigo-700 bg-opacity-30 p-4 rounded-lg text-center">
              <div className="bg-indigo-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">1</span>
              </div>
              <p className="text-indigo-100">Visit the PokéRogue website using any modern browser</p>
            </div>
            <div className="bg-indigo-700 bg-opacity-30 p-4 rounded-lg text-center">
              <div className="bg-indigo-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">2</span>
              </div>
              <p className="text-indigo-100">Click "PLAY NOW" to load the game</p>
            </div>
            <div className="bg-indigo-700 bg-opacity-30 p-4 rounded-lg text-center">
              <div className="bg-indigo-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">3</span>
              </div>
              <p className="text-indigo-100">Login or register a new account</p>
            </div>
            <div className="bg-indigo-700 bg-opacity-30 p-4 rounded-lg text-center">
              <div className="bg-indigo-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                <span className="text-white font-bold">4</span>
              </div>
              <p className="text-indigo-100">Choose "NEW GAME" and enjoy!</p>
            </div>
          </div>
        </div>

        <div className="bg-indigo-900 bg-opacity-30 p-8 rounded-lg">
          <h3 className="text-2xl font-bold text-white mb-6">Game Controls</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-semibold text-indigo-300 mb-4">General Controls</h4>
              <ul className="space-y-3 text-indigo-100">
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">ENTER/SPACE/Z</span>
                  <span>Select options or advance dialogue</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">BACKSPACE/X</span>
                  <span>Cancel options or advance dialogue</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">ESC/M</span>
                  <span>Open or close the game's menu</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">+/-</span>
                  <span>Speed up or slow down the game</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold text-indigo-300 mb-4">Starter Screen Controls</h4>
              <ul className="space-y-3 text-indigo-100">
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">ENTER</span>
                  <span>Start the run without navigating to the "Start" button</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">R</span>
                  <span>Cycle between shiny and non-shiny sprites</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">F</span>
                  <span>Cycle available forms</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">G</span>
                  <span>Cycle available genders</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">E</span>
                  <span>Cycle available abilities</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 px-2 py-1 rounded text-sm mr-3 inline-block min-w-20 text-center">N</span>
                  <span>Cycle available natures</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gameplay and Guide */}
      <section className="py-16 bg-indigo-900 bg-opacity-30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">Gameplay & Guide</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <BookOpen className="mr-3 text-indigo-400" />
                Gameplay Overview
              </h3>
              <p className="text-indigo-200 mb-4">
                PokéRogue is a game that combines the world of Pokémon with a genre called a "roguelite." Roguelike games are known for their difficulty and permadeath, where if your character dies, you start the game over from the beginning. While PokéRogue doesn't exactly follow the permadeath aspect, it does have some features that make each playthrough different and challenging.
              </p>
              <p className="text-indigo-200 mb-4">
                As you progress, you'll face challenging decisions that enhance your team's power but also increase the difficulty. This risk-reward system keeps the gameplay fresh and exciting, forcing you to constantly adapt your strategies.
              </p>
              <p className="text-indigo-200">
                Navigating PokéRogue is intuitive for those familiar with Pokémon games. Use the arrow keys to move your character, interact with the environment with the Z key, and access your menu with the X key. Battles follow the classic turn-based format, with options to attack, use items, switch Pokémon, or run from the encounter.
              </p>
            </div>
            
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Heart className="mr-3 text-indigo-400" />
                Items in PokéRogue
              </h3>
              <ul className="space-y-4 text-indigo-200">
                <li>
                  <span className="text-white font-semibold">Pokéballs:</span> Essential for capturing Pokémon, there might be different Pokéball types with varying catch rates.
                </li>
                <li>
                  <span className="text-white font-semibold">Healing Items:</span> Potions, Revives, and Full Restores to keep your Pokémon in top shape.
                </li>
                <li>
                  <span className="text-white font-semibold">Stat-Boosting Items:</span> Items that temporarily or permanently increase a Pokémon's stats like attack, defense, or speed.
                </li>
                <li>
                  <span className="text-white font-semibold">Evolution Stones:</span> Items like Fire Stones or Water Stones to evolve certain Pokémon.
                </li>
                <li>
                  <span className="text-white font-semibold">Battle Items:</span> Items like X-Attack or X-Defense to enhance your Pokémon's performance in battle.
                </li>
                <li>
                  <span className="text-white font-semibold">Held Items:</span> Items your Pokémon can hold to grant them passive benefits.
                </li>
                <li>
                  <span className="text-white font-semibold">Key Items:</span> Special items required to progress through the game or access certain areas.
                </li>
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-8">
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4">Stages and Challenges</h3>
              <p className="text-indigo-200 mb-4">
                PokéRogue throws you into a series of stages, each one a battle against wild Pokémon, trainers, or powerful bosses. Wild battles are most common, but trainers can pop up almost anywhere. Boss battles mark the end of each biome (more on those later) and legendary encounters.
              </p>
              <p className="text-indigo-200 mb-4">
                Before facing wild Pokémon, you can strategize and switch your Pokémon to match the opponent. This flexibility is crucial because stat changes you apply to your Pokémon persist between wild battles! However, this freedom disappears against trainers. Here, your team is locked in, and any stat buffs or debuffs reset before the fight. Trainer battles also test your knowledge, as their Pokémon prioritize super-effective moves and switch out to exploit type advantages. Wild Pokémon, on the other hand, rely on unpredictable, random attacks.
              </p>
            </div>
            
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4">Exploring PokéRogue Biomes</h3>
              <p className="text-indigo-200 mb-4">
                Every ten stages take you to a new PokéRogue biome, each with its own theme and a distinct set of Pokémon. The first two biomes are familiar territory – a bustling town and sprawling plains – filled with common Pokémon like Normals, Grass-types, and Bugs.
              </p>
              <p className="text-indigo-200 mb-4">
                The climax of each biome (stage 10, 20, etc.) throws you into a challenging battle against either a gym leader from the main PokéRogue game or a powerful boss Pokémon. In my experience, bosses appear more frequently than gym leaders. Both opponents thematically match their environment. Facing the "cave" biome? Expect rock-type gym leaders or a cave-dwelling boss!
              </p>
              <p className="text-indigo-200">
                Victory comes with rewards! You'll heal your entire party after defeating the leader or boss, and receive a charm to boost your Pokémon's EXP gain. Gym leader victories also grant you egg tickets.
              </p>
            </div>
            
            <div className="bg-indigo-800 bg-opacity-50 p-6 rounded-lg">
              <h3 className="text-2xl font-bold text-white mb-4">Your Persistent Rival</h3>
              <p className="text-indigo-200 mb-4">
                Get ready for a recurring challenger! Throughout your journey, you'll face your rival at specific stages – 8, 25, 55, and so on.
              </p>
              <ul className="list-disc pl-6 text-indigo-200 mb-4">
                <li>Your first encounter happens around stage 8.</li>
                <li>Defeat him, and he rewards you with an EXP charm and an EXP Share, which grants a percentage of your battling Pokémon's EXP to the rest of your team.</li>
                <li>Each subsequent victory over your rival earns you another EXP charm.</li>
              </ul>
              <p className="text-indigo-200">
                Buckle up, though, because later encounters pit you against his boss Pokémon and even a Mega Rayquaza!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">Frequently Asked Questions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">Is PokéRogue free to play?</h3>
            <p className="text-indigo-200">Yes, PokéRogue is completely free to play in your web browser. No downloads or purchases are required to enjoy the full game experience.</p>
          </div>
          
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">Can I play PokéRogue on mobile?</h3>
            <p className="text-indigo-200">Yes, PokéRogue works on mobile browsers, although the experience is optimized for desktop. The touch controls are functional but may take some getting used to.</p>
          </div>
          
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">Do I need to create an account?</h3>
            <p className="text-indigo-200">While you can play as a guest, creating an account allows you to save your progress, participate in leaderboards, and access additional features.</p>
          </div>
          
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">What makes PokéRogue different from regular Pokémon games?</h3>
            <p className="text-indigo-200">PokéRogue features procedurally generated worlds, meaning each playthrough is unique. It also has a higher difficulty level and requires more strategic thinking than traditional Pokémon games.</p>
          </div>
          
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">Is PokéRogue an official Pokémon game?</h3>
            <p className="text-indigo-200">No, PokéRogue is a fan-made project and not officially affiliated with Nintendo or The Pokémon Company.</p>
          </div>
          
          <div className="bg-indigo-800 bg-opacity-30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">How often is PokéRogue updated?</h3>
            <p className="text-indigo-200">The development team regularly updates PokéRogue with new features, balance changes, and bug fixes. Check the game's GitHub page for the latest updates.</p>
          </div>
        </div>
      </section>

      <GoogleAdsense slot="1234567890" style={{ display: 'block', marginTop: '2rem' }} />
    </div>
  );
};

export default PokeRogue;
